import { botPServedFromId, botPServedFromToken, getSystemBots, isBotPServed, type PublicBotInfo } from '@wui/im'

import { Contact } from '@/shared/types/api'
import { RoomListItem } from '@/shared/types/app'
import { fromPServedUser, numberToUri } from '@/shared/utils/uri'

import { getSystemBotContact } from './system-bots'

const normalizeBotSearchText = (value: string): string => {
  return value.toLowerCase().replace(/[-_<>:@.\s]/g, '')
}

const BOT_SEARCH_QUERY_RE = /^@([A-Za-z0-9][\w.:-]*)$/

export const parseBotSearchToken = (query: string): string | undefined => {
  const match = query.trim().match(BOT_SEARCH_QUERY_RE)
  return match?.[1]
}

export const getBotPServedFromSearchQuery = (query: string): string | undefined => {
  const token = parseBotSearchToken(query)
  if (!token) {
    return undefined
  }

  try {
    const pServed = token.includes(':') ? botPServedFromToken(token) : botPServedFromId(token)
    const systemBot = getSystemBots().find(bot => bot.pServed.toLowerCase() === pServed.toLowerCase())
    return systemBot?.pServed ?? pServed
  } catch {
    return undefined
  }
}

export const matchesBotTokenSearch = (query: string, pServed: string): boolean => {
  const botPServed = getBotPServedFromSearchQuery(query)
  if (!botPServed) {
    return false
  }

  return botPServed.toLowerCase() === pServed.toLowerCase()
}

export const isBotPlaceholderName = (name: string, pServed: string): boolean => {
  const token = pServed.match(/BOT-([^@>]+)/i)?.[1]
  if (!token) {
    return false
  }

  return name === token || name.toLowerCase() === `bot-${token}`.toLowerCase()
}

export const userBotToContact = (pServed: string, name: string): Contact => ({
  id: pServed,
  name,
  internalNumber: '',
  groupIds: [],
  organization: null,
  position: null,
  imLogin: pServed,
  terminalLogin: '',
  terminalPassword: '',
})

export const isBotMemberNumber = (value: string): boolean => {
  return isBotPServed(value) || value.startsWith('BOT-')
}

export const toBotPServed = (value: string): string => {
  if (isBotPServed(value)) {
    return value
  }

  return numberToUri(value)
}

export const hasRealBotName = (name: string | undefined, pServed: string): boolean => {
  const trimmed = name?.trim()
  if (!trimmed) {
    return false
  }

  return !isBotPlaceholderName(trimmed, pServed)
}

export const pickBetterNamedContact = (pServed: string, ...contacts: Array<Contact | undefined>): Contact | undefined => {
  const defined = contacts.filter((contact): contact is Contact => Boolean(contact))
  return defined.find(contact => hasRealBotName(contact.name, pServed)) ?? defined[0]
}

export const resolveBotContact = (pServed: string, name?: string): Contact => {
  const systemBot = getSystemBotContact(pServed)
  if (systemBot) {
    return systemBot
  }

  const fallbackName = name?.trim() || fromPServedUser(pServed)
  return userBotToContact(pServed, fallbackName)
}

export const isBotCatalogSearchQuery = (query: string): boolean => {
  return query.trim().startsWith('@')
}

export const matchesPublicBotSearch = (
  query: string,
  bot: Pick<PublicBotInfo, 'name' | 'publicIdentity' | 'description' | 'pServed'>,
): boolean => {
  const rest = isBotCatalogSearchQuery(query) ? query.trim().slice(1) : query.trim()
  if (!rest) {
    return true
  }

  const lower = rest.toLowerCase()
  const normalized = normalizeBotSearchText(rest)
  const fields = [bot.name, bot.publicIdentity, bot.description ?? '', bot.pServed ?? '']

  return fields.some(field => {
    const fieldLower = field.toLowerCase()
    return fieldLower.includes(lower) || normalizeBotSearchText(field).includes(normalized)
  })
}

export const resolvePublicBotPServed = (bot: PublicBotInfo): string | undefined => {
  if (bot.pServed) {
    return bot.pServed
  }

  try {
    return botPServedFromId(bot.publicIdentity)
  } catch {
    return undefined
  }
}

export const publicBotToContact = (bot: PublicBotInfo, pServed: string): Contact => ({
  id: pServed,
  name: bot.name,
  internalNumber: '',
  groupIds: [],
  organization: bot.description ?? null,
  position: null,
  imLogin: pServed,
  terminalLogin: '',
  terminalPassword: '',
})

export const publicBotToRoomListItem = (bot: PublicBotInfo): RoomListItem | undefined => {
  const pServed = resolvePublicBotPServed(bot)
  if (!pServed) {
    return undefined
  }

  const contact = publicBotToContact(bot, pServed)
  const subscribers = new Map<string, Contact>()
  subscribers.set(contact.id, contact)

  return {
    id: pServed,
    roomId: pServed,
    pServedUser: pServed,
    name: bot.name,
    number: '',
    isGroup: false,
    subscribers,
    isEnabled: true,
  }
}
